import React, { Suspense } from 'react'
import CacheBuster from 'react-cache-buster'
import { BrowserRouter } from 'react-router-dom'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import packageJson from '../package.json'
import appConfig from 'app.config'
import {
	ErrorFallbackPage,
	logError,
	UseAuthProvider,
	CircleSpinner,
} from 'exports'

import { MyPagesContextProvider } from './contexts/MyPagesContext'
import RootRoute from 'constants/routes/RoutRoute'
import Footer from 'components/Footer/Footer'
import Header from 'components/header/Header'
import './styling/_theme.scss'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fab } from '@fortawesome/free-brands-svg-icons'
import {
	faHome,
	faHandHoldingUsd,
	faPiggyBank,
	faComments,
	faUser,
	faSignOutAlt,
	faTimes,
	faBars,
	faArrowLeft,
	faFileInvoice,
	faCaretDown,
	faEdit,
	faAt,
	faAddressCard,
	faAngleLeft,
	faSortDown,
	faSortUp,
	faChevronDown,
	faChevronUp,
	faDownload,
	faPhoneAlt,
	faEnvelope,
	faSignInAlt,
	faCookieBite,
	faInfoCircle,
	faExclamationTriangle,
	faTimesCircle,
	faTrashAlt,
	faSearchDollar,
} from '@fortawesome/free-solid-svg-icons'
import { MyPagesCustomerInfoContextProvider } from './contexts/MyPagesCustomerInfoContext'
import WebChat from './components/webChat/WebChat'
library.add(
	fab,
	faHome,
	faHandHoldingUsd,
	faPiggyBank,
	faComments,
	faUser,
	faSignOutAlt,
	faTimes,
	faBars,
	faArrowLeft,
	faFileInvoice,
	faCaretDown,
	faEdit,
	faAt,
	faAddressCard,
	faAngleLeft,
	faExclamationTriangle,
	faChevronDown,
	faChevronUp,
	faDownload,
	faPhoneAlt,
	faEnvelope,
	faSignInAlt,
	faCookieBite,
	faInfoCircle,
	faSortUp,
	faSortDown,
	faTimesCircle,
	faTrashAlt,
	faSearchDollar,
)

const MetaTags = ({ isEnvProd }: any) => {
	const description = document.querySelector('meta[name="description"]')
	if (description) {
		description.remove()
	}
	const getTitle = () => {
		switch (appConfig.country) {
			case 'dk':
				return 'My Pages | TF Bank'
			case 'ee':
				return 'TF Bank iseteenindus'
			case 'lt':
				return 'Savitarna | TF Bank'
			case 'lv':
				return 'Portāls | TF Bank'
			case 'fi':
				return 'Omat sivut | TF Bank'
			case 'se':
				return 'Mina Sidor | TF Bank'
			default:
				return 'My Pages | TF Bank'
		}
	}

	const getDesc = () => {
		switch (appConfig.country) {
			case 'dk':
				return 'My Pages | TF Bank'
			case 'ee':
				return 'Vaata infot oma arve, maksegraafiku või laenujäägi kohta'
			case 'lt':
			case 'lv':
				return 'My Pages TF Bank AB'
			case 'fi':
				return 'Tarkista oman laina- tai säästötuotteesi tiedot TF Bankin Omilta sivuilta vuorokauden ympäri. Kirjaudu sisään!'
			case 'se':
				return 'Mina Sidor TF Bank AB'
			default:
				return 'My Pages TF Bank AB'
		}
	}

	const getMeta = () => {
		if (isEnvProd) {
			return [
				{
					name: 'description',
					content: `${getDesc()}`,
				},
			]
		} else {
			return [
				{
					name: 'description',
					content: `${getDesc()}`,
				},
				{
					name: 'robots',
					content: 'none',
				},
			]
		}
	}

	return <Helmet title={getTitle()} meta={getMeta()} />
}

function errorFallback({ error, resetErrorBoundary }: FallbackProps) {
	return (
		<ErrorFallbackPage
			error={error}
			resetErrorBoundary={resetErrorBoundary}
		/>
	)
}

const App: React.FC = () => {
	const isEnvProd = process.env.REACT_APP_ENVIRONMENT === 'production'
	let version = packageJson.version

	return (
		<HelmetProvider>
			<BrowserRouter>
				<div className={`App ${appConfig.country}`}>
					<Suspense fallback={<CircleSpinner />}>
						<ErrorBoundary
							fallbackRender={errorFallback}
							onError={(error: Error) => logError(error)}>
							<CacheBuster
								currentVersion={version}
								isEnabled={true} //If false, the library is disabled.
								isVerboseMode={false} //If true, the library writes verbose logs to console.
								loadingComponent={<CircleSpinner />} //If not pass, nothing appears at the time of new version check.
							>
								<UseAuthProvider>
									<MetaTags isEnvProd={isEnvProd} />
									<MyPagesContextProvider>
										<MyPagesCustomerInfoContextProvider>
											<Header />
											<WebChat></WebChat>
											<RootRoute></RootRoute>
											<Footer />
										</MyPagesCustomerInfoContextProvider>
									</MyPagesContextProvider>
								</UseAuthProvider>
							</CacheBuster>
						</ErrorBoundary>
					</Suspense>
				</div>
			</BrowserRouter>
		</HelmetProvider>
	)
}

export default App
